import store from "@/state/store";
import {
  getTestRoleId,
  isLocalhost,
  getAuthEndpoint as getAuthEndpointFromHelper,
} from "@/helpers/roleHelper";

/**
 * Daftar service yang hanya tersedia di admin endpoint
 * Service ini akan selalu menggunakan /v1/admin/ prefix
 */
const ADMIN_ONLY_SERVICES = [
  "tiers",
  "brands",
  "spots",

  "departments",
  "itemRewards",
  "productCategories",
  "admin",
  "doctors",
  "staffClinic",
  "roles",

  "promos",

  "loyaltyPrograms",
  "questioners",
  "questionerGroups",
  "jobs",
  "faqs",
  "contentManagement",
  "serviceCenters",
  "reports",
];

/**
 * Helper untuk mendapatkan endpoint berdasarkan role user
 * @param {string} service - Nama service (orders, customers, products, dll)
 * @param {number} roleId - Role ID (opsional, jika tidak disediakan akan mengambil dari store)
 * @param {boolean} forceAdmin - Force menggunakan admin endpoint (opsional)
 * @returns {string} Endpoint lengkap dengan prefix role
 */
export const getRoleBasedEndpoint = (
  service,
  roleId = null,
  forceAdmin = false
) => {
  // Jika roleId tidak disediakan, ambil dari store
  if (!roleId) {
    roleId = store.getters["auth/currentRoleId"];
  }

  // Untuk testing lokal, gunakan roleId dari helper
  if (isLocalhost()) {
    roleId = getTestRoleId() || roleId;
  }

  // Jika service hanya tersedia di admin atau forceAdmin = true, gunakan admin endpoint
  if (forceAdmin || ADMIN_ONLY_SERVICES.includes(service)) {
    return `/v1/admin/${service}`;
  }

  // Tentukan prefix berdasarkan roleId
  let prefix;
  switch (roleId) {
    case 1: // Super Admin
      prefix = "admin";
      break;
    case 2: // Clinic Staff
      prefix = "staff";
      break;
    case 3: // Doctor
      prefix = "doctor";
      break;
    default:
      prefix = "admin"; // Fallback ke admin
  }

  return `/v1/${prefix}/${service}`;
};

/**
 * Helper untuk mendapatkan endpoint auth berdasarkan role
 * @param {number} roleId - Role ID (opsional)
 * @returns {string} Auth endpoint
 */
export const getAuthEndpoint = () => {
  // Untuk auth endpoint, gunakan helper dari roleHelper.js yang sudah menangani subdomain
  return getAuthEndpointFromHelper();
};

/**
 * Helper untuk mengecek apakah user memiliki akses ke service tertentu
 * @param {string} service - Nama service
 * @param {number} roleId - Role ID (opsional)
 * @returns {boolean} Apakah user memiliki akses
 */
export const hasServiceAccess = (service, roleId = null) => {
  if (!roleId) {
    roleId = store.getters["auth/currentRoleId"];
  }

  // Mapping service ke role yang diizinkan
  const serviceAccessMap = {
    orders: [1, 2], // Super Admin & Staff
    customers: [1, 2], // Super Admin & Staff
    claimRewards: [1, 2], // Super Admin & Staff
    consultations: [1, 2, 3], // Hanya Super Admin
    poinHistory: [1, 2],
    products: [1, 3], // Hanya Super Admin
    promos: [1], // Hanya Super Admin
    treatments: [1, 3], // Hanya Super Admin
    loyaltyPrograms: [1], // Hanya Super Admin
    questioners: [1], // Hanya Super Admin
    questionerGroups: [1], // Hanya Super Admin
    jobs: [1], // Hanya Super Admin
    faqs: [1], // Hanya Super Admin
    contentManagement: [1], // Hanya Super Admin
    serviceCenters: [1], // Hanya Super Admin
    messages: [1, 2, 3], // Semua role
    reports: [1], // Hanya Super Admin
    admin: [1], // Hanya Super Admin
    doctors: [1], // Hanya Super Admin
    staffClinic: [1], // Hanya Super Admin
    roles: [1], // Hanya Super Admin
    masterData: [1], // Hanya Super Admin
    // Service yang hanya tersedia di admin
    tiers: [1, 2], // Super Admin & Staff (tapi endpoint tetap admin)
    brands: [1], // Hanya Super Admin
    spots: [1], // Hanya Super Admin
    polyclinics: [1, 3], // Tambahkan role 3 (Doctor)
    specializations: [1, 3], // Tambahkan role 3 (Doctor)
    departments: [1], // Hanya Super Admin
    itemRewards: [1], // Hanya Super Admin
    productCategories: [1], // Hanya Super Admin
    chat: [1, 2, 3],
    upload: [1], // Hanya Super Admin
  };

  const allowedRoles = serviceAccessMap[service] || [];
  return allowedRoles.includes(roleId);
};

/**
 * Helper untuk mendapatkan role prefix berdasarkan roleId
 * @param {number} roleId - Role ID
 * @returns {string} Role prefix (admin, staff, doctor)
 */
export const getRolePrefix = (roleId) => {
  switch (roleId) {
    case 1:
      return "admin";
    case 2:
      return "staff";
    case 3:
      return "doctor";
    default:
      return "admin";
  }
};

/**
 * Helper untuk mengecek apakah service hanya tersedia di admin endpoint
 * @param {string} service - Nama service
 * @returns {boolean} Apakah service hanya tersedia di admin
 */
export const isAdminOnlyService = (service) => {
  return ADMIN_ONLY_SERVICES.includes(service);
};
